import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { createSessionSchemaFactory } from '@/schemas/sessions/create-session.ts';
import TimePicker from '@/components/common/time-picker';
import SessionTypeInfo from '@/components/common/session-type-info';
import { format, parse, addMinutes, startOfDay } from 'date-fns';
import { Timelapse } from '@mui/icons-material';
import { MAX_SESSION_DURATION_MINUTES } from '@/constants';
import OffsetButtons from '@/components/flows/detail/slot-list/slot-timing/offset-buttons';
import { useEventStore } from '@/state/stores/use-event-store';
import { useOfferedSessionTypes } from '@/hooks/use-offered-session-types';
import { getObjectId } from '@/helpers/object.id.helper';

export type SessionFormType = z.infer<ReturnType<typeof createSessionSchemaFactory>>;

interface AddSessionFormProps {
  onSubmit: (data: SessionFormType) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  maxDuration?: number;
  defaultIsInLibrary?: boolean;
}

export const AddSessionForm = ({
  onSubmit,
  onCancel,
  maxDuration,
  isLoading = false,
  defaultIsInLibrary = false,
}: AddSessionFormProps) => {
  const t = useTranslations();

  const event = useEventStore((s) => s.event);
  const multiFlowEvent = event?.multiFlow;
  const eventOriginId = event?.origin ? getObjectId(event.origin) : event?.id;
  const { selectableSessionTypes } = useOfferedSessionTypes(eventOriginId);

  const createSessionSchema = createSessionSchemaFactory(t);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SessionFormType>({
    resolver: zodResolver(createSessionSchema),
    defaultValues: {
      type: '',
      title: '',
      plannedDurationMinutes: '00:30',
      isInLibrary: defaultIsInLibrary,
    },
  });

  const maxTime = addMinutes(startOfDay(new Date()), maxDuration ?? MAX_SESSION_DURATION_MINUTES);

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} sx={{ flex: 1 }}>
      <Stack spacing={1}>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Box sx={{ width: '100%' }}>
              <TextField
                {...field}
                fullWidth
                variant="standard"
                label={t('session.form.titleInputLabel')}
                placeholder={t('session.form.titleInputPlaceholder')}
                error={!!errors.title}
                helperText={errors.title?.message}
                disabled={isLoading}
              />
            </Box>
          )}
        />
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth variant="standard" error={!!errors.type}>
                <InputLabel>{t('session.form.typeInputLabel')}</InputLabel>
                <Select {...field}>
                  <MenuItem value="" disabled>
                    {t('session.form.selectType')}
                  </MenuItem>
                  {selectableSessionTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      <SessionTypeInfo type={type} />
                    </MenuItem>
                  ))}
                </Select>
                {errors.type && (
                  <Typography variant="caption" color="error">
                    {errors.type.message}
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}
        />
        <Controller
          name="plannedDurationMinutes"
          control={control}
          render={({ field }) => (
            <Stack direction="row" spacing={1} alignItems="flex-end" width="100%">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TimePicker
                  label={t('session.form.plannedDurationInputLabel')}
                  ampm={false}
                  views={['hours', 'minutes']}
                  maxTime={maxTime}
                  value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
                  durationMode={true}
                  icon={Timelapse}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(format(value, 'HH:mm'));
                    } else {
                      field.onChange(null);
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'standard',
                      error: !!errors.plannedDurationMinutes,
                      helperText: errors.plannedDurationMinutes?.message,
                      sx: { minWidth: 0 },
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                <OffsetButtons offsetSize={1} currentValue={field.value || ''} onClick={(val) => field.onChange(val)} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                <OffsetButtons offsetSize={5} currentValue={field.value || ''} onClick={(val) => field.onChange(val)} />
              </Box>
            </Stack>
          )}
        />
        {!defaultIsInLibrary && multiFlowEvent && (
          <Controller
            name="isInLibrary"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isLoading} />}
                label={t('session.form.addToLibraryLabel')}
              />
            )}
          />
        )}
      </Stack>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
        <Button variant="outlined" color="info" onClick={onCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          data-testid="save-new-session-button"
          variant="contained"
          color="info"
          loading={isLoading}
        >
          {t('common.create')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default AddSessionForm;
